import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusAndNote } from "../StatusAndNote";
import { Add, Delete, InfoOutlined } from "@mui/icons-material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { PlanoDeTratamentoHistory } from "./PlanodeTratamentoHistory";

import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import InputText from "@/components/form/InputText";
import { Controller } from "react-hook-form";
import { useQuestionnaireContext } from "../../../../contexts/QuestionnaireContext";
import { useSessionContext } from "../../../../contexts/SessionContext";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { scrollbarStyle } from "../scrollbarStyle";
import LuxorMed from "../../../../components/luxor/LuxorMed";
import { toast } from "react-toastify";
import { useAttendanceSpecialtyIndex } from "@/hooks/useAttendanceSpecialtyIndex";
import { buildQrDisplayInfo } from "@/utils/resolveAttendanceSpecialty";

export function PlanosDeTratamento() {
  const styleType: any = {
    "long-term": "#183B56",
    "short-term": "#5F6C85",
  };

  const { response, loadingResponse } = useQuestionnaireContext();
  const specialtyIndex = useAttendanceSpecialtyIndex();
  const { control, unregister, carePlan, setCarePlan, luxorMode, getValues, setValue } =
    useMedicalCareContext();
  const { user } = useSessionContext();
  const { state } = useLocation();

  const responseEntries = response?.entry?.map((el: any) => el?.resource);

  const treatmentPlansResponses = responseEntries?.map((el: any) => {
    const awnserDate = el?.authored;
    const filterItens = el?.item
      ?.filter((e: any) => e?.text === "Planos de tratamento")?.[0]
      ?.item?.filter((subItem: any) => {
        return subItem?.item?.[0]?.answer;
      });
    const displayInfo = buildQrDisplayInfo(el, specialtyIndex);
    return { awnserDate, filterItens, displayInfo };
  });

  const flatOpenItens = treatmentPlansResponses
    ?.filter((el: any) => el.filterItens?.length && el.filterItens)
    ?.flatMap((item: any) => {
      const auxItem = item?.filterItens?.map((response: any) => {
        return {
          ...response,
          displayInfo: item?.displayInfo,
          awnserDate: item?.awnserDate,
        };
      });

      return auxItem;
    });

  // Abordagem simplificada: apenas itens atuais ou mais recentes não interagidos
  const auxVerify = flatOpenItens?.filter((item: any) => {
    const value = item?.item?.[0]?.answer?.[0]?.valueString;
    const isCurrentEncounter = item?.displayInfo?.encounterId === state?.encounterReference?.id;
    const hasStatus = !!item?.item?.[2]?.answer?.[0]?.valueCoding; // Note que o status está no índice 2 para PlanosDeTratamento
    const hasNote = !!item?.item?.[3]?.answer?.[0]?.valueString;
    
    // Itens do encounter atual sempre aparecem
    if (isCurrentEncounter) {
      return true;
    }
    
    // Para itens de encounters anteriores:
    
    // 1. Encontre todos os itens com o mesmo valor/plano
    const sameItems = flatOpenItens?.filter((el: any) => 
      el?.item?.[0]?.answer?.[0]?.valueString === value
    );
    
    // 2. Ordene por data (mais recentes primeiro)
    const sortedItems = [...sameItems].sort((a, b) => 
      new Date(b.awnserDate).getTime() - new Date(a.awnserDate).getTime()
    );
    
    // 3. Verifique se o item atual é o mais recente
    const isLatestItem = sortedItems[0]?.displayInfo?.encounterId === item?.displayInfo?.encounterId;
    
    // 4. Contagem total de itens para este plano
    const totalCount = sortedItems.length;
    const isPair = totalCount % 2 === 0;
    
    // 5. Verifique se já existe algum item com status para este plano
    const anyItemHasStatus = sameItems.some(
      (el: any) => !!el?.item?.[2]?.answer?.[0]?.valueCoding // Status no índice 2
    );
        
    // Regras de exibição:
    // - Se não for o item mais recente, não mostrar
    if (!isLatestItem) {
      return false;
    }
    
    // - Se for par E já tiver algum item com status, não mostrar
    if (isPair && anyItemHasStatus) {
      return false;
    }
    
    // - Se for ímpar, mostrar
    return !isPair || !anyItemHasStatus;
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addNewPlan();
    }
  };
  
  // Função para verificar se um plano já existe
  const checkIfPlanExists = (planText: string, currentIndex: number): boolean => {
    if (!planText?.trim()) {
      return false;
    }

    const currentValues = getValues();
    
    // Encontra os índices válidos com base nos elementos não-undefined
    const validIndices = carePlan.map((item: any, i: number) => item !== undefined ? i : -1).filter((i: number) => i !== -1);
    
    // Verifica se o plano já existe na lista atual
    const existsInCurrentList = validIndices.some((index: number) => {
      const planValue = currentValues?.newPlan?.[index]?.plano;
      return planValue === planText && index !== currentIndex;
    });
    
    // Verifica se já existe nos planos abertos
    const flatOpenItens = document.querySelectorAll('.lista-planos-abertos .MuiTypography-root');
    const existsInOpenList = Array.from(flatOpenItens).some(
      (item: Element) => item.textContent?.trim() === planText.trim()
    );

    return existsInCurrentList || existsInOpenList;
  };

  // Função para lidar com a validação ao alterar o texto do plano
  const handlePlanChange = (value: string, index: number) => {
    // Não verificar se o valor for vazio ou muito curto
    if (!value || value.length < 3) return;
    
    // Usar setTimeout para criar um debounce simples
    setTimeout(() => {
      // Verificar se o texto atual no campo é o mesmo que estamos verificando
      // para evitar problemas com digitação rápida
      const currentValue = getValues(`newPlan.${index}.plano`);
      if (currentValue !== value) return; // Texto mudou enquanto processava
      
      if (checkIfPlanExists(value, index)) {
        // Se já existe, mostrar um toast de aviso
        toast.warning(`"${value}" já está na lista de planos de tratamento`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
      // Limpa o campo completamente
      setValue(`newPlan.${index}.plano`, undefined);
      setValue(`newPlan.${index}.prazo`, undefined);
      
      // Assegurar que o formulário reconheça a mudança forçando uma atualização
      setTimeout(() => {
        setValue(`newPlan.${index}.plano`, undefined);
      }, 0);
      }
    }, 2000); // 2000ms de debounce
  };
    
  // Função para verificar se um plano já existe e adicionar novo plano
  const addNewPlan = () => {
    const currentValues = getValues();
    
    // Encontra o último índice válido com base nos elementos não-undefined
    const validIndices = carePlan.map((item: any, i: number) => item !== undefined ? i : -1).filter((i: number) => i !== -1);
    const lastIndex = validIndices.length > 0 ? Math.max(...validIndices) : -1;
    
    // Obtém o plano atual do formulário
    const currentPlan = currentValues?.newPlan?.[lastIndex]?.plano;
    
    // Se não tem texto, não faz nada
    if (!currentPlan?.trim()) {
      return;
    }
    
    // Verifica se o plano já existe
    if (checkIfPlanExists(currentPlan, lastIndex)) {
      // Se já existe, mostrar um toast de aviso
      toast.warning(`"${currentPlan}" já está na lista de planos de tratamento`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Limpa o campo completamente
      setValue(`newPlan.${lastIndex}.plano`, undefined);
      setValue(`newPlan.${lastIndex}.prazo`, undefined);
      
      // Assegurar que o formulário reconheça a mudança forçando uma atualização
      setTimeout(() => {
        setValue(`newPlan.${lastIndex}.plano`, undefined);
      }, 0);
    } else {
      // Se não existe, adicionar normalmente
      // Filtra qualquer undefined que possa existir no array antes de adicionar o novo item
      const filteredCarePlan = carePlan.filter((item: any) => item !== undefined);
      setCarePlan([...filteredCarePlan, ""]);
    }
  };

  return (
    <>
      <Stack
        spacing={1}
        width="100%"
        sx={{
          maxHeight: "60vh",
          overflow: "auto",
          padding: 2,
          ...scrollbarStyle,
        }}
      >
        {carePlan?.map((treatmentPlan: any, index: number) => (
          <>
            {treatmentPlan !== undefined && (
              <>
                <Controller
                  name={`newPlan.${index}.prazo`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <RadioGroup
                        row
                        aria-labelledby="demo-controlled-radio-buttons-group"
                        aria-label="position"
                        sx={{ width: "100%" }}
                        {...field}
                        value={field.value ?? ""}
                      >
                        <FormControlLabel
                          key={1}
                          value={1}
                          control={<Radio />}
                          label="Longo prazo"
                        />
                        <FormControlLabel
                          key={2}
                          value={2}
                          control={<Radio />}
                          label="Curto prazo"
                        />
                      </RadioGroup>
                    </>
                  )}
                />

                <Stack
                  width="100%"
                  justifyContent="space-between"
                  spacing={1}
                  direction="row"
                >
                  <InputText
                    key={index}
                    label="Plano de tratamento"
                    name={`newPlan.${index}.plano`}
                    control={control}
                    autoFocus
                    onKeyDown={handleKeyDown}
                    handleChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePlanChange(e.target.value, index)}
                    multiline={true}
                    defaultValue={treatmentPlan || ""}
                  />
                  {carePlan?.filter((el: any) => el !== undefined)?.length >
                    1 && (
                    <IconButton
                      onClick={() => {
                        setCarePlan((prevItems: any) => {
                          // Filtra os itens definidos (remove os undefined e o item atual)
                          return prevItems.filter((item: any, i: number) => 
                            item !== undefined && i !== index
                          );
                        });
                        unregister(`newPlan.${index}.plano`);
                        unregister(`newPlan.${index}.prazo`);
                      }}
                    >
                      <Delete sx={{ color: "red" }} />
                    </IconButton>
                  )}
                </Stack>
              </>
            )}
          </>
        ))}

        <Stack width="100%" direction="row" justifyContent="right">
            <Button
              sx={{ width: "fit-content" }}
              onClick={addNewPlan}
              variant="text"
            >
              <Add /> Novo plano
            </Button>
        </Stack>

        <Divider orientation="horizontal" />
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            variant="body1"
            sx={{ my: 2, fontWeight: 500, fontSize: 16, color: "#555" }}
          >
            Lista de Planos em aberto
          </Typography>
          <Chip
            label={auxVerify?.length}
            size={"small"}
            sx={{ fontWeight: 500, fontSize: 12 }}
          />
        </Stack>
        
        <div className="lista-planos-abertos">
        {auxVerify?.map((el: any, index: number) => {
          return (
            <Accordion
              defaultExpanded={index === 0}
              sx={{
                width: "100%",
                boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
                position: "initial",
                border: "1px solid",
                borderColor: "neutral200.main",
                borderRadius: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<GridExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Stack
                  direction="row"
                  width="100%"
                  justifyContent="space-between"
                >
                  {el?.item?.[0]?.answer?.[0]?.valueString.length > 100 ? (
                    <Tooltip
                      placement="right"
                      title={el?.item?.[0]?.answer?.[0]?.valueString}
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [-20, -40],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography
                        fontWeight={500}
                        sx={{ color: "#555", wordBreak: "break-word" }}
                      >
                        {el?.item?.[0]?.answer?.[0]?.valueString.substring(
                          0,
                          70
                        )}
                        ...
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography fontWeight={500} sx={{ color: "#555" }}>
                      {el?.item?.[0]?.answer?.[0]?.valueString}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2}>
                    <Chip
                      label={el?.item?.[1]?.answer?.[0]?.valueCoding?.display}
                      size="small"
                      variant="outlined"
                      sx={{
                        color:
                          styleType[
                            el?.item?.[1]?.answer?.[0]?.valueCoding?.code
                          ],
                        borderColor:
                          styleType[
                            el?.item?.[1]?.answer?.[0]?.valueCoding?.code
                          ],
                        borderRadius: 2,
                        marginBottom: 1,
                        width: "fit-content",
                      }}
                    />
                    {el?.displayInfo?.healthcare && (
                      <Chip
                        label={
                          el?.displayInfo?.healthcare || "Sem especialidade"
                        }
                        size="small"
                        sx={{
                          color: el?.displayInfo?.healthcare
                            ? "neutral700.main"
                            : "neutral500.main",

                          fontWeight: 500,
                          backgroundColor: el?.displayInfo?.healthcare
                            ? ""
                            : "#f1f1f1",
                          width: "fit-content",
                        }}
                      />
                    )}
                    <Chip
                      label={dayjs(el?.awnserDate)
                        ?.format("DD MMM YYYY")
                        ?.toUpperCase()}
                      size="small"
                      sx={{ fontWeight: 500, fontSize: 12, color: "#555" }}
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <StatusAndNote
                  index={index}
                  type="CarePlans"
                  answer={`${el?.item?.[0]?.answer?.[0]?.valueString};${el?.item?.[1]?.answer?.[0]?.valueCoding?.code}`}
                />
                {/* {el?.displayInfo?.identifier === user?.username ? (
                  <StatusAndNote
                    index={index}
                    type="CarePlans"
                    answer={`${el?.item?.[0]?.answer?.[0]?.valueString};${el?.item?.[1]?.answer?.[0]?.valueCoding?.code}`}
                  />
                ) : (
                  <Stack
                    direction="row"
                    //bgcolor="primary200.main"
                    // bgcolor={bgColor ? `${bgColor}` : "transparent"}
                    alignItems="center"
                    spacing={1}
                    width="100%"
                    height="60px"
                    justifyContent="left"
                  >
                    <InfoOutlined />
                    <Typography variant="subtitle2" fontWeight={400}>
                      Plano adicionada por outro profissional
                    </Typography>
                  </Stack>
                )} */}
              </AccordionDetails>

              <Stack
                sx={{ padding: 2 }}
                direction="row"
                width="100%"
                alignItems="center"
              >
                {el?.displayInfo?.practitioner && (
                  <Typography
                    color="neutral700.main"
                    sx={{ fontWeight: 500, fontStyle: "italic", fontSize: 12 }}
                  >
                    Dr. {el?.displayInfo?.practitioner}
                  </Typography>
                )}
              </Stack>
            </Accordion>
          );
        })}
        </div>
      </Stack>
      <Stack
        width="100%"
        overflow="auto"
        maxHeight="60vh"
        paddingRight={3}
        sx={{ ...scrollbarStyle }}
      >
        {!luxorMode && <PlanoDeTratamentoHistory />}
        {luxorMode && <LuxorMed />}
      </Stack>
    </>
  );
}
